import { BuildingOffice, CaretDown, File, HouseLine, UserGear } from '@phosphor-icons/react'
import { getTime, parseISO } from 'date-fns'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import API from '../../api/api'
import { APP_COLORS, ONE_DAY_DURATION } from '../../helpers/constants'
import Badge from '../Badge/Badge'
import DoughnutChart from '../Charts/DoughnutChart/DoughnutChart'

const CHART_COLORS = [APP_COLORS.grey400, APP_COLORS.blue500, APP_COLORS.red500]

const INCREASE_COLOR = APP_COLORS.green500
const DECREASE_COLOR = APP_COLORS.red500

const getPeriod = (daysAmount, t) => {
  let end = new Date()
  let start = new Date(end.getTime() - daysAmount * ONE_DAY_DURATION)

  return {
    value: { start: start.toISOString(), end: end.toISOString() },
    title: t('internal-inspections:analytics.lastNDays', { count: daysAmount }),
    daysAmount,
  }
}

const getPeriods = (t) => [getPeriod(7, t), getPeriod(30, t), getPeriod(60, t), getPeriod(90, t)]

const InspectionsAnalytics = ({ buildingId }) => {
  const { t } = useTranslation(['internal-inspections'])
  const periods = getPeriods(t)
  const defaultAnalyticsPeriod = periods[1]
  const [analyticsData, setAnalyticsData] = useState(null)

  const [period, setPeriod] = useState(defaultAnalyticsPeriod)

  useEffect(() => {
    ;(async () => {
      if (!buildingId) return
      const response = await API.getInspectionAnalytics(buildingId, {
        startDate: getTime(parseISO(period.value.start)),
        endDate: getTime(parseISO(period.value.end)),
      })
      setAnalyticsData(response.data)
    })()
  }, [buildingId, period])

  const handleSelectPeriodHandler = (e) => {
    setPeriod(JSON.parse(e.target.value))
  }

  if (!analyticsData) return null

  const getDirectionSymbol = (change) => {
    return change > 0 ? '▲' : change < 0 ? '▼' : ''
  }

  const getDirectionColor = (change) => {
    return change > 0 ? INCREASE_COLOR : change < 0 ? DECREASE_COLOR : APP_COLORS.black500
  }

  const formatChange = (change) => {
    return `${Math.abs(change).toFixed(1)}%`
  }

  const doughnutChartData = {
    labels: [
      t('internal-inspections:filters.completed'),
      t('internal-inspections:filters.upcoming'),
      t('internal-inspections:filters.overDue'),
    ],
    datasets: [
      {
        data: [
          analyticsData.inspectionsData.completed,
          analyticsData.inspectionsData.upcoming,
          analyticsData.inspectionsData.overDue,
        ],
        backgroundColor: CHART_COLORS,
        borderColor: CHART_COLORS,
        borderWidth: 0,
        diff: [
          analyticsData.inspectionsData.changes.completed,
          analyticsData.inspectionsData.changes.upcoming,
          analyticsData.inspectionsData.changes.overDue,
        ],
      },
    ],
  }

  const doughnutChartOptions = {
    legend: {
      position: 'right',
      labels: {
        boxWidth: 14,
        padding: 20,
        fontSize: 14,
        fontFamily: 'Montserrat',
        fontColor: APP_COLORS.black800,
      },
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: (tooltipItem, data) => ` ${data.labels[tooltipItem.index]}`,
        afterLabel: function (tooltipItem, data) {
          const dataset = data.datasets[0]
          const value = dataset.data[tooltipItem.index]
          const change = dataset.diff[tooltipItem.index]
          const symbol = getDirectionSymbol(change)

          return ` ${value} ${symbol} ${formatChange(change)}`
        },
      },
      caretSize: 0,
      backgroundColor: APP_COLORS.baseWhite,
      titleFontSize: 16,
      borderColor: APP_COLORS.grey400,
      cornerRadius: 6,
      borderWidth: 1,
      titleFontColor: APP_COLORS.black800,
      bodyFontColor: APP_COLORS.black800,
      bodyFontSize: 14,
    },
    plugins: {
      innerText: true,
      datalabels: {
        display: false,
      },
      doughnutlabel: {
        labels: [
          {
            text: analyticsData.inspectionsData.total.toString(),
            font: {
              size: '24',
              weight: 'bold',
            },
            color: APP_COLORS.black800,
          },
          {
            // Format with direction symbol
            text: `${getDirectionSymbol(
              analyticsData.inspectionsData.changes.total,
            )} ${formatChange(analyticsData.inspectionsData.changes.total)}`,
            font: {
              size: '14',
              weight: 'normal',
            },
            // Color based on direction
            color: getDirectionColor(analyticsData.inspectionsData.changes.total),
          },
        ],
      },
    },
  }

  return (
    <div className="ctp-internal-inspections__analytics">
      <div className="ctp-internal-inspections__analytics__header">
        <h2>{t('internal-inspections:analytics.title')}</h2>
        <div className="ctp-internal-inspections__analytics__header__range-select">
          <label className="select">
            <select
              id="period"
              name="period"
              defaultValue={JSON.stringify(defaultAnalyticsPeriod)}
              className={`select select-input`}
              onChange={handleSelectPeriodHandler}
            >
              {periods.map((period) => (
                <option value={JSON.stringify(period)} key={period.title}>
                  {period.title}
                </option>
              ))}
            </select>
            <CaretDown size={20} />
          </label>
          <span className="ctp-internal-inspections__analytics__header__compared-to-text">
            {t('internal-inspections:analytics.comparedToPreviousDays', {
              count: period.daysAmount,
            })}
          </span>
        </div>
      </div>

      <div className="ctp-internal-inspections__analytics__content">
        <div className="ctp-internal-inspections__analytics__content__chart-container">
          <DoughnutChart
            options={doughnutChartOptions}
            data={doughnutChartData}
            width={300}
            height={160}
          />
        </div>
        <div className="ctp-internal-inspections__analytics__content__items">
          {[
            {
              title: t('internal-inspections:analytics.unitsInspected'),
              icon: <HouseLine size={24} />,
              value: analyticsData.unitsInspected.total,
              change: analyticsData.unitsInspected.change,
            },
            {
              title: t('internal-inspections:analytics.commonAreasInspected'),
              icon: <BuildingOffice size={24} />,
              value: analyticsData.commonAreasInspected.total,
              change: analyticsData.commonAreasInspected.change,
            },
            {
              title: t('internal-inspections:analytics.reportsGenerated'),
              icon: <File size={24} />,
              value: analyticsData.reportsGenerated.total,
              change: analyticsData.reportsGenerated.change,
            },
            {
              title: t('internal-inspections:analytics.inspectionBasedWorkOrders'),
              icon: <UserGear size={24} />,
              value: analyticsData.inspectionBasedWorkOrders.total,
              change: analyticsData.inspectionBasedWorkOrders.change,
            },
          ].map((item, index) => (
            <div key={index} className="ctp-internal-inspections__analytics__content__items__item">
              <div className="ctp-internal-inspections__analytics__content__items__item-title">
                {item.icon}
                {item.title}
              </div>
              <div className="ctp-internal-inspections__analytics__content__items__item-value">
                <div>{item.value}</div>
                <Badge color={item.change > 0 ? 'green' : item.change === 0 ? 'gray' : 'red'}>
                  {getDirectionSymbol(item.change)} {item.change}%
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

InspectionsAnalytics.propTypes = {
  buildingId: PropTypes.number,
}

export default InspectionsAnalytics
